/**
 * Pingo — Gift option (cart-level).
 *
 * Owns the "is this a gift?" checkbox rendered by snippets/pingo-gift.liquid into
 * snippets/cart-summary.liquid — which BOTH the cart drawer and the cart page render,
 * so one file drives both surfaces.
 *
 * State is the CART, never the client: gift is ON iff the cart attribute
 * `Presente` == `Sim`. Liquid renders that truth on every cart re-render; this file
 * writes it. Nothing is cached locally, so there is nothing to desync.
 *
 * TWO MODES:
 *   no [data-gift-variant-id] -> flag only. One attributes write. No lines change, so
 *                                no re-render, no cart event, and no CDN module loaded.
 *   [data-gift-variant-id]    -> the cart also gains/loses the add-on line, so the
 *                                drawer, cart icon and totals must re-render.
 *
 * ONE REQUEST either way. Verified against Shopify's Cart API docs: /cart/update.js
 * ADDS a line when the variant isn't already in the cart, and accepts `attributes` in
 * the same call. So the flag and the line move together atomically — there is no
 * add-then-flag sequence that can half-succeed. Untick is the same call with qty 0.
 *
 * Delegation (not per-node binding) is deliberate: the cart re-renders through
 * morph.js, which replaces these nodes. A listener bound directly to the input would
 * die on the first re-render — a document-level listener survives.
 *
 * Vanilla rather than Alpine: a deliberate deviation from docs/FRONTEND-STACK.md,
 * approved by the owner 2026-07-15. Alpine x-data on morphed nodes is fragile, and
 * cart writes are explicitly a "Logic & Integrations -> Vanilla JS" concern in that
 * same doc. See docs/plans/gift-option.md §7.2.
 */

(function () {
  if (window.__pingoGiftInit) return;
  window.__pingoGiftInit = true;

  var ATTR_KEY = 'Presente';
  var ATTR_ON = 'Sim';

  // CLAUDE.md voice: neutral but warm. No "Ops", no apology, no blame.
  var ERROR_TEXT = 'Não foi possível atualizar o carrinho. Tente de novo.';

  // Tracks the most recent intent. Rapid toggling must settle on the LAST click,
  // not on whichever response happens to land last.
  var inFlight = null;

  function updateUrl() {
    if (window.Theme && Theme.routes && Theme.routes.cart_update_url) {
      return Theme.routes.cart_update_url;
    }
    return '/cart/update';
  }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function post(body) {
    return fetch(updateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().then(function (data) {
        // Shopify reports cart errors in the BODY (e.g. 404 "Cannot find variant"
        // when the merchant's gift product is unpublished), so a bare response.ok
        // check is not enough.
        if (!response.ok || data.status) {
          var error = new Error(
            data.description || data.message || 'cart/update failed: ' + response.status
          );
          error.cartResponse = data;
          throw error;
        }
        return data;
      });
    });
  }

  function attributesFor(on) {
    var attributes = {};
    // Empty string clears a cart attribute (Shopify Cart API).
    attributes[ATTR_KEY] = on ? ATTR_ON : '';
    return attributes;
  }

  /** Section ids the cart renders into, so the response can carry fresh HTML for them. */
  function sectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids;
  }

  /** Flag-only: nothing in the cart display changes, so nothing needs re-rendering. */
  function writeFlag(on) {
    return post({ attributes: attributesFor(on) });
  }

  /**
   * Paid: the cart gains or loses a line, so the drawer, icon and totals must update.
   *
   * Mirrors the deferred-promise pattern already proven in assets/pingo-crosssell.js:
   * dispatch a CartLinesUpdateEvent carrying a promise BEFORE the fetch, then resolve
   * it with the full cart. Native listeners await that promise.
   */
  function writeWithProduct(on, variantId) {
    var body = { attributes: attributesFor(on), updates: {} };
    body.updates[variantId] = on ? 1 : 0;

    var ids = sectionIds();
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = window.location.pathname;
    }

    // Imported lazily: flag-only mode is the launch default and must not pull a CDN
    // module it never uses. Dynamic import resolves through the theme's importmap.
    return import('@shopify/events').then(function (events) {
      var CartLinesUpdateEvent = events.CartLinesUpdateEvent;
      var deferred = CartLinesUpdateEvent.createPromise();

      // Dispatched on `document`, not on the checkbox. The listeners are registered
      // there (cart-icon.js:31, cart-drawer.js:33) and the event constructor exposes
      // no EventInit, so dispatching from a nested node would silently depend on a
      // `bubbles` default we cannot set or see.
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: on ? 'add' : 'remove',
          context: 'cart',
          lines: [{ merchandiseId: String(variantId), quantity: on ? 1 : 0 }],
          promise: deferred.promise,
        })
      );

      return post(body)
        .then(function (data) {
          // The FULL cart is fetched because createCartFromAjaxResponse needs
          // cart-level currency, which the update response omits (same reason
          // pingo-crosssell.js re-fetches).
          return fetch('/cart.js')
            .then(function (r) {
              return r.json();
            })
            .then(function (fullCart) {
              deferred.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart),
                detail: {
                  items: fullCart.items,
                  source: 'pingo-gift',
                  sourceId: 'pingo-gift',
                  itemCount: fullCart.item_count,
                  variantId: variantId,
                  sections: data.sections,
                  didError: false,
                },
              });
              return data;
            });
        })
        .catch(function (error) {
          // Native listeners are awaiting this promise; leaving it pending would
          // hang the cart UI rather than fail it.
          deferred.reject(error);
          throw error;
        });
    });
  }

  /**
   * Paint every gift control on the page. On /cart the drawer and the page summary
   * are both live, so ticking one must move the other or they visibly disagree.
   */
  function paint(on) {
    var wraps = document.querySelectorAll('[data-pingo-gift]');

    for (var i = 0; i < wraps.length; i++) {
      var wrap = wraps[i];
      wrap.dataset.giftOn = String(on);
      wrap.classList.toggle('pingo-gift--on', on);

      var input = wrap.querySelector('input[type="checkbox"]');
      if (input && input.checked !== on) input.checked = on;
    }
  }

  function showError(show) {
    var slots = document.querySelectorAll('[data-pingo-gift-error]');
    for (var i = 0; i < slots.length; i++) {
      slots[i].textContent = show ? ERROR_TEXT : '';
      slots[i].classList.toggle('pingo-gift__error--shown', show);
    }
  }

  /**
   * The reveal. The open/close animation itself is CSS (grid-template-rows, driven by
   * [data-gift-on] which paint() already set) — this only adds what CSS cannot: the
   * one-shot highlight and focus.
   */
  function revealed(wrap) {
    var note = wrap.parentElement
      ? wrap.parentElement.querySelector('[data-pingo-gift-note]')
      : null;
    if (!note) return;

    if (!reducedMotion()) {
      // Restart the animation on re-tick: removing the class alone won't replay it
      // until the element has been reflowed without it.
      note.classList.remove('pingo-gift-note--flash');
      void note.offsetWidth;
      note.classList.add('pingo-gift-note--flash');
    }

    // Desktop only. Focusing on mobile throws the keyboard up over the cart — the
    // exact startle the owner asked us to avoid.
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    var field = note.querySelector('textarea');
    if (!field) return;

    // Wait out the reveal, or focus lands on a field that is still zero-height.
    window.setTimeout(
      function () {
        field.focus({ preventScroll: true });
      },
      reducedMotion() ? 0 : 340
    );
  }

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox') return;
    var wrap = input.closest('[data-pingo-gift]');
    if (!wrap) return;

    var on = input.checked;
    var variantId = wrap.dataset.giftVariantId || '';

    // Optimistic: the control must feel instant, so paint before the network.
    showError(false);
    paint(on);
    if (on) revealed(wrap);

    var token = {};
    inFlight = token;

    var work = variantId ? writeWithProduct(on, variantId) : writeFlag(on);

    work
      .then(function () {
        if (inFlight !== token) return; // superseded by a later click
        inFlight = null;
      })
      .catch(function (error) {
        if (inFlight !== token) return;
        inFlight = null;

        // Revert. Never leave the box looking ticked while the cart says otherwise —
        // a silent disagreement here reaches the order.
        paint(!on);
        showError(true);
        console.error('[pingo-gift]', error);
      });
  });
})();
